import { ChevronRight } from "lucide-react";
import { Private } from "../../components/Privacy";
import type { InsightCategory, InsightMerchant, InsightTotals } from "./insight-types";
import { figure, signedFigure, tickerClass } from "./insight-format";

interface Mover { kind: string; name: string; change: number | null; label: string; onOpen?: () => void }

/** "Top movers": the overall change in one line, then the category and merchant that drove it. */
export default function InsightExplanation({ totals, categories, merchants, currency, baseline, onOpenCategory, onOpenMerchant, onOpenSpending }: {
  totals: InsightTotals; categories: InsightCategory[]; merchants: InsightMerchant[]; currency: string; baseline: string;
  onOpenCategory?: (category: string) => void; onOpenMerchant?: (merchantKey: string) => void; onOpenSpending?: () => void;
}) {
  const known = [...categories, ...merchants].every((row) => row.change !== null);
  const driver = <T extends { change: number | null }>(rows: T[], direction: number) => rows
    .filter((r) => (r.change ?? 0) * direction > 0).sort((a, b) => Math.abs(b.change ?? 0) - Math.abs(a.change ?? 0))[0];
  const increase = known ? driver(categories, 1) : undefined;
  const decrease = known ? driver(categories, -1) : undefined;
  const merchant = known ? driver(merchants, (totals.change ?? 0) >= 0 ? 1 : -1) : undefined;

  const movers: Mover[] = [];
  if (increase) {
    movers.push({ kind: "Top category rise", name: increase.name, change: increase.change, label: `View ${increase.name} category`, onOpen: onOpenCategory ? () => onOpenCategory(increase.name) : undefined });
  }
  if (decrease) {
    movers.push({ kind: "Top category drop", name: decrease.name, change: decrease.change, label: `View ${decrease.name} category`, onOpen: onOpenCategory ? () => onOpenCategory(decrease.name) : undefined });
  }
  if (merchant) {
    movers.push({ kind: (merchant.change ?? 0) > 0 ? "Top merchant rise" : "Top merchant drop", name: merchant.name, change: merchant.change, label: `View ${merchant.name} merchant`, onOpen: onOpenMerchant ? () => onOpenMerchant(merchant.key) : undefined });
  }

  return (
    <section className="panel insight-panel insight-movers insight-explanation">
      <div className="section-heading">
        <div><h2>What changed</h2><p>Against {baseline} · {currency}</p></div>
        {onOpenSpending && totals.spending !== null && (
          <button type="button" className="insight-text-button" onClick={onOpenSpending}>View spending</button>
        )}
      </div>
      {totals.change === null ? (
        <p className="insight-note">{totals.previousCount ? "A complete comparison needs exchange rates for all spending entries." : "There isn’t enough recorded spending in every comparison month to explain a change yet."}</p>
      ) : (
        <>
          <p className="insight-movers-summary">
            {totals.change === 0 ? `Spending matches ${baseline}.` : (
              <>Spending is <strong className={`insight-ticker ${tickerClass(totals.change)}`}><Private>{figure(Math.abs(totals.change), currency)}</Private> {totals.change > 0 ? "higher" : "lower"}</strong>.</>
            )}
          </p>
          {movers.length > 0 && (
            <ul className="insight-movers-list">
              {movers.map((m) => (
                <li key={m.kind}>
                  <button type="button" onClick={m.onOpen} aria-label={m.label}>
                    <span className={`insight-mover-arrow ${tickerClass(m.change)}`} aria-hidden="true">{(m.change ?? 0) > 0 ? "▲" : "▼"}</span>
                    <span className="insight-mover-name"><strong>{m.name}</strong><small>{m.kind}</small></span>
                    <span className={`insight-ticker ${tickerClass(m.change)}`}><Private>{signedFigure(m.change, currency)}</Private></span>
                    <ChevronRight size={14} className="insight-row-arrow" aria-hidden="true" />
                  </button>
                </li>
              ))}
            </ul>
          )}
          <p className="insight-note">Merchant and category changes describe the same purchases.</p>
        </>
      )}
    </section>
  );
}
